import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useRequestPasswordReset } from '../hooks/useRequestPasswordReset';
import {
  requestPasswordResetSchema,
  type RequestPasswordResetFormValues,
} from '../schemas/password-reset-schema';
import { publicPaths } from '@/app/router/paths/publicPaths';
import appIcon from '@/assets/icons/icon.svg';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/shared/components/ui/form';
import { Input } from '@/shared/components/ui/input';
import { UnifiedButton } from '@/shared/components/ui/unified-button';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const { requestReset, isLoading, isSuccess, isError, errorMessage } = useRequestPasswordReset();

  const form = useForm<RequestPasswordResetFormValues>({
    resolver: zodResolver(requestPasswordResetSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (values: RequestPasswordResetFormValues) => {
    await requestReset(values);
  };

  const onGoBack = () => {
    navigate(publicPaths.LOGIN);
  };

  if (isSuccess) {
    return (
      <div className="w-screen h-screen flex justify-center items-center">
        <div
          className="flex flex-col justify-center items-center p-0 gap-10 max-w-md w-full px-4"
          data-testid="forgot-password-success"
        >
          <div className="flex flex-col gap-4">
            <div className="max-w-[13rem]">
              <img src={appIcon} alt="Login Logo" />
            </div>
            <div className="flex flex-col items-center p-0 gap-2">
              <p className="text-welcome-subtitle text-base">Revisa tu correo electrónico</p>
            </div>
          </div>
          <div className="w-full flex flex-col items-flex-start gap-6">
            <p
              className="text-sm text-center text-welcome-subtitle"
              data-testid="forgot-password-generic-message"
            >
              Si el correo está registrado, recibirás un mensaje con instrucciones.
            </p>
            <div className="w-full bg-primary/5 rounded-lg p-4 border border-primary/10">
              <p className="text-xs text-welcome-subtitle text-center">
                <strong className="text-light">Nota:</strong> Si no encuentras el correo en tu
                bandeja de entrada, revisa tu carpeta de spam. El enlace expira en 30 minutos.
              </p>
            </div>
          </div>
          <div className="flex w-full flex-col items-center gap-4">
            <button
              type="button"
              className="text-primary text-sm cursor-pointer py-2 bg-transparent border-0"
              onClick={onGoBack}
              data-testid="forgot-password-back-link"
            >
              Volver al inicio de sesión
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-screen h-screen flex justify-center items-center">
      <div className="flex flex-col justify-center items-center p-0 gap-10 max-w-md w-full px-4">
        <div className="flex flex-col gap-6 items-center">
          <div className="max-w-[13rem]">
            <img src={appIcon} alt="Login Logo" />
          </div>
          <div className="flex flex-col items-center p-0 gap-2">
            <p className="text-welcome-subtitle text-base">Recuperar contraseña</p>
            <p className="text-welcome-subtitle text-xs text-center">
              Ingresa tu correo electrónico y te enviaremos un enlace para restablecer tu
              contraseña.
            </p>
          </div>
        </div>
        <div className="w-full flex flex-col items-flex-start gap-6">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col gap-6 w-full"
              data-testid="forgot-password-form"
            >
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="Correo electrónico"
                        type="email"
                        data-testid="forgot-password-email-input"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {isError && (
                <p className="text-sm text-negative" data-testid="forgot-password-error">
                  {errorMessage || 'Error desconocido'}
                </p>
              )}
              <div className="flex w-full flex-col items-center gap-4">
                <UnifiedButton
                  buttonType="prominent"
                  size="prominent-lg"
                  className="w-full"
                  type="submit"
                  disabled={isLoading}
                  data-testid="forgot-password-submit-button"
                >
                  {isLoading ? 'Enviando...' : 'Enviar enlace de restablecimiento'}
                </UnifiedButton>
                <button
                  type="button"
                  className="text-primary text-sm cursor-pointer py-2 bg-transparent border-0"
                  onClick={onGoBack}
                  data-testid="forgot-password-back-link"
                >
                  Volver al inicio de sesión
                </button>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
}
